import {
    Cards,
    CompanyIntroduction,
    ContentSection,
    CountryFlag,
    CustomerStoryContent,
    CustomerStoryLayout,
    Hero,
    NumberedList,
    Text,
} from '~modules/customer-stories';
import { SEO } from '~modules/layout';
import theme from '~styles/theme';

const CUSTOMER_NAME = 'Pixelfield';
const CUSTOMER_STORY_ID = 'pixelfield';

export const Head = props => (
    <SEO
        title='Pixelfield simplifies project-based access management with Passwd'
        description='Learn how Pixelfield centralized secrets and streamlined project-based access management with Passwd and Google Workspace.'
        {...props}
    />
);

const CustomerStoryPage = () => {
    return (
        <CustomerStoryLayout customerStoryId={CUSTOMER_STORY_ID}>
            <Hero customerName={CUSTOMER_NAME} customerLogoWidth={220} />
            <CustomerStoryContent>
                <CompanyIntroduction
                    description='<strong>Pixelfield</strong> is a software development agency that helps clients turn ideas into digital products. Rather than following a one-size-fits-all approach, the team tailors its development process to each client’s unique business needs, delivering solutions that balance technology with practical outcomes. Working on multiple client projects simultaneously means securely managing project-specific credentials is essential.'
                    items={[
                        { title: 'Client', content: CUSTOMER_NAME },
                        { title: 'Total users', content: '50+' },
                        { title: 'HQ', content: 'Czech Republic', contentPrefix: <CountryFlag code='CZ' /> },
                        { title: 'Customer since', content: '2026' },
                        {
                            title: 'Previous solution',
                            content: 'Notion pages with restricted access',
                        },
                    ]}
                />

                <ContentSection title='Challenges' backgroundColor={theme.colors.background.secondary}>
                    <Text
                        type='solution'
                        text='Before adopting Passwd, Pixelfield stored credentials in restricted Notion pages. While access could be limited, the solution wasn’t designed for secure secret management and became increasingly difficult to maintain. The team needed to:'
                    />
                    <NumberedList
                        items={[
                            {
                                title: 'Centralized secret management',
                                description:
                                    'Sensitive credentials needed to be centralized in a secure environment.',
                            },
                            {
                                title: 'Project-based access',
                                description: 'Access to secrets needed to be granted on a per-project basis.',
                            },
                            {
                                title: 'Simplified user management',
                                description:
                                    'User management needed to stay simple as team members joined or changed projects.',
                            },
                            {
                                title: 'Reduced documentation risk',
                                description:
                                    'The risks associated with storing passwords in documentation tools needed to be eliminated.',
                            },
                        ]}
                    />
                </ContentSection>

                <ContentSection title='Solution'>
                    <Text
                        type='solution'
                        text='After evaluating password managers such as 1Password, Pixelfield chose <strong>Passwd</strong> because of its tight integration with Google Workspace. Key decision factors included:'
                    />

                    <Cards
                        type='grid'
                        cards={[
                            {
                                title: 'Google Workspace & Google Groups integration',
                                text: 'Access is automatically managed through existing groups.',
                                image: { imageName: 'storage-icon', alt: '' },
                            },
                            {
                                title: 'Project-based permissions',
                                text: 'Each secret can be shared only with the users who need it.',
                                image: { imageName: 'lock-in-shield-icon', alt: '' },
                            },
                            {
                                title: 'Simple user experience',
                                text: 'Easy to adopt with virtually no learning curve.',
                                image: { imageName: 'users-icon', alt: '' },
                            },
                            {
                                title: 'Centralized secret management',
                                text: 'All credentials are stored securely in one place.',
                                image: { imageName: 'charts-icon', alt: '' },
                            },
                        ]}
                    />
                </ContentSection>

                <ContentSection title='Implementation' backgroundColor={theme.colors.background.secondary}>
                    <Text
                        type='implementation'
                        text='Implementation was straightforward. Since user groups were already managed in Google Workspace, connecting Passwd to the existing environment required minimal effort. Permissions could immediately be assigned based on Google Groups, giving teams access to the right credentials without additional administration.'
                    />
                </ContentSection>

                <ContentSection title='Results'>
                    <Text
                        type='results'
                        text='Pixelfield continues to use <strong>Passwd</strong> to securely manage client credentials and project access, relying on Google Workspace integration to keep permissions organized as projects and teams evolve.'
                    />

                    <Cards
                        type='carousel'
                        cards={[
                            {
                                title: 'Project-specific access control',
                                text: 'Credentials are now shared securely with only the users working on each project, improving both security and day-to-day collaboration.',
                                image: { imageName: 'lock-in-shield-icon', alt: '' },
                            },
                            {
                                title: 'Centralized secret management',
                                text: 'Sensitive information has been moved out of Notion into a dedicated password management platform built for secure credential storage.',
                                image: { imageName: 'charts-icon', alt: '' },
                            },
                            {
                                title: 'Simplified user management',
                                text: 'Because Passwd integrates directly with Google Workspace and Google Groups, adding or removing access is fast and requires little manual work.',
                                image: { imageName: 'users-icon', alt: '' },
                            },
                            {
                                title: 'Meets the team’s needs',
                                text: 'Passwd delivers exactly what Pixelfield was looking for—a simple, reliable solution that integrates naturally with the tools the team already uses.',
                                image: { imageName: 'storage-icon', alt: '' },
                            },
                        ]}
                    />
                </ContentSection>

                <ContentSection backgroundColor={theme.colors.accent.primary}>
                    <Text
                        type='conclusion'
                        text='“If you use Google Workspace, Passwd is the best match for your team.”'
                    />
                </ContentSection>
            </CustomerStoryContent>
        </CustomerStoryLayout>
    );
};

export default CustomerStoryPage;
